'use client';

import { createContext, useCallback, useContext, useMemo, useRef, type PropsWithChildren } from 'react';

import { PULSE_CONSENT_CHANGED_EVENT } from '../consent.js';
import { createTracker, type PulseTracker } from '../tracker.js';
import type { PulseTrackerConfig } from '../types.js';

import { resolveConsent } from './consent-reader.js';
import { readCookieConsent } from './read-cookie-consent.js';
import { usePulseConsentWithdrawal } from './usePulseConsentWithdrawal.js';

const PulseContext = createContext<PulseTracker | null>(null);

/**
 * The consent-changed event this provider listens to, for `PulsePageView`: a view held for consent
 * has to hear the same save the withdrawal listener hears. A second context rather than a field on
 * the first, so `usePulseTracker()` and its type stay exactly what they were. Not exported from the
 * entry.
 */
const PulseConsentChangedEventContext = createContext<string | null>(PULSE_CONSENT_CHANGED_EVENT);

type PulseProviderProps = PropsWithChildren<{
    /**
     * Optional. With nothing passed, events go to `/api/pulse/track` — the path the route handler
     * is documented to live at — and carry no default context.
     *
     * In practice every consumer sets `defaultContext.surface`, because that is the only thing
     * distinguishing two frontends of the same `source_type` and it cannot be added retroactively.
     */
    config?: PulseTrackerConfig;

    /**
     * The `window` event this surface's cookie banner dispatches when preferences are saved.
     * Defaults to Cineamo's, which is also what {@link syncPulseConsent} dispatches. A surface with a
     * different banner names its own; passing `null` disables the withdrawal listener entirely, for
     * a surface whose consent cannot change without a reload. The cookie it watches is
     * `config.consentCookieName`. `PulsePageView` listens to the same event to send a view it held
     * for consent, and stops listening with it.
     */
    consentChangedEvent?: string | null;
}>;

/**
 * Mounts a tracker for the subtree.
 *
 * Touches no dynamic API — no cookies, no headers, no `searchParams` — so putting it in a root
 * layout does not opt the app out of static generation, ISR or PPR. A partner whose caching
 * quietly breaks the day they add analytics rips the analytics out, so that stays a guarantee
 * rather than an accident.
 */
export function PulseProvider({
    config,
    consentChangedEvent = PULSE_CONSENT_CHANGED_EVENT,
    children
}: PulseProviderProps) {
    // The memo is keyed on the config's *value*, not its identity.
    //
    // `config` is optional and defaulted now, so the ergonomic call is an inline literal —
    // `<PulseProvider config={{ defaultContext: { surface: 'www' } }}>` — which is a new
    // object on every render of the layout. Keyed on identity, that rebuilt the tracker and the
    // context value each time and re-rendered every usePulseTracker consumer in the tree. Both
    // in-repo apps hand-wrote a useMemo around their config to compensate; a third party has no
    // reason to guess, and the failure is a performance cliff with no error attached.
    //
    // The memo builds from the caller's real object rather than a JSON round trip of it, so a
    // defaultContext value that does not survive serialization still arrives intact. The ref is
    // for the consent reader below, which must see the latest render's `consent`. Writing it
    // during render is safe here because it is idempotent.
    const configRef = useRef(config);
    configRef.current = config;

    const configKey = JSON.stringify(config ?? {});

    // Consent is the one field the key cannot see (functions do not serialize), so it is read
    // through the ref on every call rather than captured when the tracker is built. A reader backed
    // by React or CMP-hook state therefore follows its latest render. Stable for the provider's
    // lifetime, so it never rebuilds the tracker or re-subscribes the withdrawal listener. The
    // cookie fallback reads `consentCookieName` through the same ref, so the tracker and the
    // withdrawal listener always watch one cookie.
    const readConsent = useCallback(
        () => resolveConsent(configRef.current?.consent, () => readCookieConsent(configRef.current?.consentCookieName)),
        []
    );

    const tracker = useMemo(
        () => createTracker({ ...config, consent: readConsent }),
        // Keyed on the value, not on `config`'s identity — see the comment at the top.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [configKey, readConsent]
    );

    // Mounted here rather than left to each consumer's provider tree: a withdrawal that only some
    // surfaces send is worse than none, because the ones that do not keep an identity marked
    // granted for a visitor who has said stop. See usePulseConsentWithdrawal.
    useConsentWithdrawal(tracker, consentChangedEvent, readConsent);

    return (
        <PulseContext.Provider value={tracker}>
            <PulseConsentChangedEventContext.Provider value={consentChangedEvent}>
                {children}
            </PulseConsentChangedEventContext.Provider>
        </PulseContext.Provider>
    );
}

/**
 * Wraps the withdrawal listener so `null` can switch it off without breaking the rules of hooks —
 * the hook is always called; the event name it listens to is what varies.
 */
function useConsentWithdrawal(tracker: PulseTracker, eventName: string | null, readConsent: () => boolean): void {
    usePulseConsentWithdrawal(tracker, {
        eventName: eventName ?? undefined,
        disabled: eventName === null,
        readConsent
    });
}

/**
 * Access the pulse tracker from any component within PulseProvider.
 */
export function usePulseTracker(): PulseTracker {
    const tracker = useContext(PulseContext);
    if (!tracker) {
        throw new Error('usePulseTracker must be used within a PulseProvider');
    }
    return tracker;
}

/**
 * Same as usePulseTracker, but returns null instead of throwing when there is no PulseProvider.
 *
 * For shared components that want to report a pulse event where a provider exists (the consumer
 * apps) without breaking the apps that never mount one — the operator tools render the same
 * components but have no pulse pipeline. Tracking is optional; rendering is not.
 */
export function usePulseTrackerOptional(): PulseTracker | null {
    return useContext(PulseContext);
}

/** The provider's consent-changed event name, `null` when it listens to none. Internal to the package. */
export function usePulseConsentChangedEvent(): string | null {
    return useContext(PulseConsentChangedEventContext);
}
